// Moves a screen between Branches within the same org. A screen belongs to
// exactly one branch; the branch gates WHO sees it on the dashboard (its
// members). Transferring repoints the screen's branch — it doesn't touch the
// device itself; pairing, content, and license all carry over.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, ArrowLeftRight, FolderOpen, Check } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import {
  getBranches, assignScreenToBranch, type Branch,
} from '@meno/ui/lib/api'

interface Props {
  open:        boolean
  screenId:    number
  onClose:     () => void
  /** Called after a successful transfer so the parent can refetch the
   *  screen's branch membership / refresh the page header etc. */
  onTransferred?: (newBranchId: number) => void
}

export default function TransferScreenDialog({
  open, screenId, onClose, onTransferred,
}: Props) {
  const { t } = useTranslation()
  const [branches, setBranches]   = useState<Branch[]>([])
  const [loading, setLoading]     = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setSelectedId(null)
    getBranches()
      .then(r => setBranches(r.data))
      .catch(() => toast.error(t('transfer.loadFailed', 'Could not load branches.')))
      .finally(() => setLoading(false))
  }, [open, t])

  // Assigning a screen to a branch repoints its branch_id (a move, idempotent).
  const canSubmit = selectedId !== null && !submitting

  const submit = async () => {
    if (!canSubmit || selectedId === null) return
    setSubmitting(true)
    try {
      await assignScreenToBranch(selectedId, screenId)
      toast.success(t('transfer.success', 'Screen moved to the selected branch.'))
      onTransferred?.(selectedId)
      onClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('transfer.failed', 'Transfer failed. Try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-primary" />
            {t('transfer.title', 'Transfer Screen')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'transfer.description',
              'Move the screen to a different branch. Access may change — only members of the destination branch will see it on their dashboards.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          ) : branches.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('transfer.noBranches', 'No branches available. Create one from Branches first.')}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {branches.map(s => {
                const isSelected = selectedId === s.id
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      className={[
                        'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border text-start transition-colors',
                        isSelected
                          ? 'border-primary bg-muted'
                          : 'border-border hover:bg-muted/40',
                      ].join(' ')}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FolderOpen className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium truncate">{s.name}</span>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit} className="gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('transfer.submit', 'Transfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
